// packages/front/src/ideas/ideas.hooks.ts
//
// TanStack Query server-state layer for the Ideas feature.
// All reads use useApiQuery, all mutations use useApiMutation / useMutation
// with key-based invalidation in onSuccess. No ad-hoc useState+useEffect+
// apiFetch for server-state. Query keys come from the single registry `qk`.
//
// Invalidation matrix (onSuccess of every mutation):
//   idea CRUD → ideas only (positions untouched)

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, qk, useApiMutation, useApiQuery } from "../_common";
import type {
  Idea,
  IdeaTransaction,
  ItemsResponse,
} from "./ideas.types";

// Re-export types so the page-component imports them from hooks (single import site).
export type { Idea, IdeaTransaction, ItemsResponse };

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Read: ideas list (optionally filtered by date range). */
export function useIdeas(from?: string, to?: string) {
  const sp = new URLSearchParams();
  if (from) sp.set("from", from);
  if (to) sp.set("to", to);
  const qs = sp.toString();
  return useApiQuery<ItemsResponse<Idea>>(
    qk.ideas.all,
    `/api/ideas${qs ? `?${qs}` : ""}`,
  );
}

/** Read: transactions for an idea's symbol (GET /api/ideas/:id/transactions). */
export function useIdeaTransactions(id: string | null | undefined) {
  return useApiQuery<ItemsResponse<IdeaTransaction>>(
    qk.ideas.transactions(id ?? ""),
    `/api/ideas/${id}/transactions`,
    { enabled: Boolean(id) },
  );
}

// ---------------------------------------------------------------------------
// Mutations — Ideas
// ---------------------------------------------------------------------------

/** Create an idea. Invalidates ideas. */
export function useCreateIdea() {
  return useApiMutation<Idea, Idea>("/api/ideas", "POST", {
    invalidateKeys: [qk.ideas.all],
  });
}

/** Update an idea by id. Invalidates ideas. */
export function useUpdateIdea() {
  const queryClient = useQueryClient();
  return useMutation<Idea, Error, { id: string; body: Partial<Idea> }>({
    mutationFn: ({ id, body }) =>
      apiFetch<Idea>(`/api/ideas/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.ideas.all });
    },
  });
}

/** Delete an idea by id. Invalidates ideas. */
export function useDeleteIdea() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/ideas/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.ideas.all });
    },
  });
}
